import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

// Split into two contexts to avoid re-rendering control-only consumers on every tick.
// TimelineTimeContext updates at 20 Hz during playback (currentTime).
// TimelineControlContext holds stable values (play, pause, seek, bounds, isPlaying, etc.)
// that only change on user actions or playback start/stop.
const TimelineTimeContext = createContext(null);
const TimelineControlContext = createContext(null);

// Legacy combined context kept for backward compatibility with useTimeline()
const TimelineContext = createContext(null);

function formatSkipDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;
  return remainMinutes > 0 ? `${hours}h ${remainMinutes}m` : `${hours}h`;
}

export function TimelineProvider({
  bounds, recordings = [], events = [],
  skipEmptyTime = false, highlightReelMode = false, highlightReelLag = 5,
  children,
}) {
  // The caller rebuilds `bounds` and the flattened `events` array on every one
  // of its renders. Keying anything here on those object identities tore the
  // playback interval down and rebuilt it each time — losing the elapsed but
  // not yet ticked time — and re-rendered every useTimelineControls consumer,
  // which is the exact cost the split contexts exist to avoid. Everything below
  // depends on the primitives, or reads through a ref.
  const { start: boundsStart, end: boundsEnd } = bounds;
  const stableBounds = useMemo(
    () => ({ start: boundsStart, end: boundsEnd }),
    [boundsStart, boundsEnd]
  );

  const [currentTime, setCurrentTime] = useState(boundsStart);
  const [isPlaying, setIsPlaying] = useState(false);
  // Named timeWindow, not window: a state variable called `window` shadows the
  // global inside this whole component.
  const [timeWindow, setWindow] = useState(null); // { start, end } or null = full duration
  const timeRef = useRef(boundsStart);
  const intervalRef = useRef(null);
  // Track which event we last skipped to, so we don't re-toast the same one
  const lastSkippedEventIdx = useRef(-1);

  // Pre-compute sorted recording intervals for gap skipping
  const sortedRecordings = useRef([]);
  useEffect(() => {
    if (recordings.length > 0) {
      sortedRecordings.current = [...recordings]
        .sort((a, b) => a.startTime - b.startTime)
        .map((r) => ({ start: r.startTime, end: r.endTime }));
    }
  }, [recordings]);

  // Pre-compute sorted events for highlight reel, read through a ref so the
  // lookups below stay stable across a caller re-render.
  const sortedEventsRef = useRef([]);
  sortedEventsRef.current = useMemo(
    () => [...events].sort((a, b) => a.startTime - b.startTime),
    [events]
  );

  const findNextRecordingStart = useCallback((time) => {
    const next = sortedRecordings.current.find((rec) => rec.start > time);
    return next ? next.start : null;
  }, []);

  const isInAnyRecording = useCallback((time) => {
    return sortedRecordings.current.some((rec) => time >= rec.start && time <= rec.end);
  }, []);

  const isInAnyEvent = useCallback((time) => {
    return sortedEventsRef.current.some((evt) => time >= evt.startTime && time <= evt.endTime);
  }, []);

  const findNextEvent = useCallback((time) => {
    const idx = sortedEventsRef.current.findIndex((evt) => evt.startTime > time);
    return idx >= 0 ? { event: sortedEventsRef.current[idx], index: idx } : null;
  }, []);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const seek = useCallback((time) => {
    const clamped = Math.max(boundsStart, Math.min(boundsEnd, time));
    timeRef.current = clamped;
    setCurrentTime(clamped);
  }, [boundsStart, boundsEnd]);

  const play = useCallback(() => {
    lastSkippedEventIdx.current = -1;
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    stopInterval();
    setCurrentTime(timeRef.current);
    setIsPlaying(false);
  }, [stopInterval]);

  const clearWindow = useCallback(() => setWindow(null), []);

  // Drive playback with setInterval for reliable, consistent state updates
  useEffect(() => {
    if (!isPlaying) {
      stopInterval();
      return undefined;
    }

    let lastTick = performance.now();
    intervalRef.current = setInterval(() => {
      const now = performance.now();
      const delta = Math.min(now - lastTick, 200);
      lastTick = now;

      const end = timeWindow?.end ?? boundsEnd;
      let next = timeRef.current + delta;

      // Highlight reel mode: skip to next event when outside any event
      if (highlightReelMode && next < end && !isInAnyEvent(next)) {
        const nextEvt = findNextEvent(next);
        if (nextEvt && nextEvt.event.startTime <= end) {
          // Jump to (event start - lag), clamped to timeline start
          const lagMs = highlightReelLag * 1000;
          const jumpTarget = Math.max(boundsStart, nextEvt.event.startTime - lagMs);
          // Only skip if the target is actually ahead of us (avoid backward jumps from lag)
          if (jumpTarget > next) {
            // Only toast if this is a new event we haven't announced
            if (nextEvt.index !== lastSkippedEventIdx.current) {
              lastSkippedEventIdx.current = nextEvt.index;
              const skippedMs = jumpTarget - next;
              if (skippedMs > 1000) {
                toast.info(
                  `Skipped to event: ${nextEvt.event.label || 'Rule violation'}`,
                  { autoClose: 2000, hideProgressBar: true }
                );
              }
            }
            next = jumpTarget;
          }
        } else {
          // No more events — stop playback
          timeRef.current = end;
          setCurrentTime(end);
          setIsPlaying(false);
          stopInterval();
          toast.info('Highlight reel complete', { autoClose: 2000, hideProgressBar: true });
          return;
        }
      }

      // Skip empty time: if we've left all recordings, jump to the next one
      if (skipEmptyTime && !highlightReelMode && next < end && !isInAnyRecording(next)) {
        const nextStart = findNextRecordingStart(next);
        if (nextStart && nextStart <= end) {
          const skippedMs = nextStart - next;
          toast.info(`Skipped ${formatSkipDuration(skippedMs)} gap`, { autoClose: 2000, hideProgressBar: true });
          next = nextStart;
        }
      }

      if (next >= end) {
        timeRef.current = end;
        setCurrentTime(end);
        setIsPlaying(false);
        stopInterval();
      } else {
        timeRef.current = next;
        setCurrentTime(next);
      }
    }, 50);

    return () => stopInterval();
  }, [isPlaying, boundsStart, boundsEnd, timeWindow, stopInterval, skipEmptyTime, highlightReelMode,
      isInAnyRecording, findNextRecordingStart, isInAnyEvent, findNextEvent, highlightReelLag]);

  // Memoize control values so TimelineControlContext consumers only re-render
  // when play/pause state or bounds actually change — not on every 50ms tick.
  const controlValue = useMemo(() => ({
    isPlaying, seek, play, pause, bounds: stableBounds, window: timeWindow, setWindow, clearWindow,
  }), [isPlaying, seek, play, pause, stableBounds, timeWindow, setWindow, clearWindow]);

  const fullValue = useMemo(() => ({
    currentTime, isPlaying, seek, play, pause, bounds: stableBounds, window: timeWindow, setWindow, clearWindow,
  }), [currentTime, isPlaying, seek, play, pause, stableBounds, timeWindow, setWindow, clearWindow]);

  return (
    <TimelineControlContext.Provider value={controlValue}>
      <TimelineTimeContext.Provider value={currentTime}>
        <TimelineContext.Provider value={fullValue}>
          {children}
        </TimelineContext.Provider>
      </TimelineTimeContext.Provider>
    </TimelineControlContext.Provider>
  );
}

/**
 * Full context — subscribes to both time ticks AND control changes.
 * Use this only when the component needs currentTime (displays, charts, video sync).
 */
export function useTimeline() {
  const ctx = useContext(TimelineContext);
  if (!ctx) throw new Error('useTimeline must be used within TimelineProvider');
  return ctx;
}

/**
 * Controls-only context — does NOT re-render on 50ms time ticks.
 * Use this for UI that only needs play/pause/seek/bounds/isPlaying
 * (e.g., the play button, transport controls, window selectors).
 */
export function useTimelineControls() {
  const ctx = useContext(TimelineControlContext);
  if (!ctx) throw new Error('useTimelineControls must be used within TimelineProvider');
  return ctx;
}

/**
 * Time-only context — re-renders on every tick but carries no overhead
 * from control object identity changes. Use for lightweight time displays.
 */
export function useTimelineTime() {
  const time = useContext(TimelineTimeContext);
  if (time === null || time === undefined) throw new Error('useTimelineTime must be used within TimelineProvider');
  return time;
}

export default TimelineContext;
